import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

const STORAGE_KEY = 'southcrete_site_trip_v1';

const SiteTripContext = createContext(null);

function readStored() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function normalizeGuests(raw) {
  const adults = Math.min(20, Math.max(1, Number(raw?.adults) || 2));
  const children = Math.min(20, Math.max(0, Number(raw?.children) || 0));
  const infants = Math.min(10, Math.max(0, Number(raw?.infants) || 0));
  return { adults, children, infants };
}

function normalizeVisitDate(value) {
  if (value == null || value === '') return null;
  const s = String(value).trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
  const d = new Date(`${s}T12:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  return s;
}

function normalizeServiceType(value) {
  return value === 'car_rental' ? 'car_rental' : 'excursions';
}

function normalizeVisitDateRange(raw, fallbackVisitDate = null) {
  const start = normalizeVisitDate(raw?.start ?? fallbackVisitDate);
  const end = normalizeVisitDate(raw?.end);
  if (!start) return { start: null, end: null };
  if (!end) return { start, end: null };
  return end < start ? { start, end: start } : { start, end };
}

function normalizeCarRental(raw) {
  const days = Math.min(30, Math.max(1, Number(raw?.days) || 1));
  const pickupTime = /^\d{2}:\d{2}$/.test(String(raw?.pickupTime || ''))
    ? String(raw.pickupTime)
    : '10:00';
  const pickupRef = String(raw?.pickupRef || '').trim();
  return { days, pickupTime, pickupRef };
}

function normalizeLanguageId(value) {
  if (value == null || value === '' || value === '-1') return null;
  const raw = String(value).trim();
  return raw || null;
}

const DEFAULT_TRIP = {
  visitDateRange: { start: null, end: null },
  visitDate: null,
  serviceType: 'excursions',
  guests: normalizeGuests(null),
  carRental: normalizeCarRental(null),
  languageId: null,
};

function tripStateFromStored(stored) {
  const visitDateRange = normalizeVisitDateRange(stored?.visitDateRange, stored?.visitDate);
  return {
    visitDateRange,
    visitDate: visitDateRange.start,
    serviceType: normalizeServiceType(stored?.serviceType),
    guests: normalizeGuests(stored?.guests),
    carRental: normalizeCarRental(stored?.carRental),
    languageId: normalizeLanguageId(stored?.languageId),
  };
}

export function SiteTripProvider({ children }) {
  const [visitDateRange, setVisitDateRangeState] = useState(DEFAULT_TRIP.visitDateRange);
  const [visitDate, setVisitDateState] = useState(DEFAULT_TRIP.visitDate);
  const [serviceType, setServiceTypeState] = useState(DEFAULT_TRIP.serviceType);
  const [guests, setGuestsState] = useState(DEFAULT_TRIP.guests);
  const [carRental, setCarRentalState] = useState(DEFAULT_TRIP.carRental);
  const [languageId, setLanguageIdState] = useState(DEFAULT_TRIP.languageId);

  useEffect(() => {
    const stored = readStored();
    if (!stored) return;
    const next = tripStateFromStored(stored);
    setVisitDateRangeState(next.visitDateRange);
    setVisitDateState(next.visitDate);
    setServiceTypeState(next.serviceType);
    setGuestsState(next.guests);
    setCarRentalState(next.carRental);
    setLanguageIdState(next.languageId);
  }, []);

  const persist = useCallback((nextState) => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(nextState)
      );
    } catch {
      /* ignore quota */
    }
  }, []);

  const setVisitDateRange = useCallback((value) => {
    setVisitDateRangeState((prev) => {
      const next = normalizeVisitDateRange(value, prev?.start ?? null);
      const nextStart = next.start ?? null;
      setVisitDateState(nextStart);
      persist({
        visitDateRange: next,
        visitDate: nextStart,
        serviceType,
        guests,
        carRental,
        languageId,
      });
      return next;
    });
  }, [carRental, guests, languageId, persist, serviceType]);

  const setVisitDate = useCallback((value) => {
    const normalized = normalizeVisitDate(value);
    const nextRange = normalized
      ? { start: normalized, end: null }
      : { start: null, end: null };
    setVisitDateRangeState(nextRange);
    setVisitDateState(normalized);
    persist({
      visitDateRange: nextRange,
      visitDate: normalized,
      serviceType,
      guests,
      carRental,
      languageId,
    });
  }, [carRental, guests, languageId, persist, serviceType]);

  const setServiceType = useCallback((value) => {
    const normalized = normalizeServiceType(value);
    setServiceTypeState(normalized);
    persist({
      visitDateRange,
      visitDate,
      serviceType: normalized,
      guests,
      carRental,
      languageId,
    });
  }, [carRental, guests, languageId, persist, visitDate, visitDateRange]);

  const setGuests = useCallback((partial) => {
    setGuestsState((prev) => {
      const next = normalizeGuests({ ...prev, ...partial });
      persist({
        visitDateRange,
        visitDate,
        serviceType,
        guests: next,
        carRental,
        languageId,
      });
      return next;
    });
  }, [carRental, languageId, persist, serviceType, visitDate, visitDateRange]);

  const setCarRental = useCallback((partial) => {
    setCarRentalState((prev) => {
      const next = normalizeCarRental({ ...prev, ...partial });
      persist({
        visitDateRange,
        visitDate,
        serviceType,
        guests,
        carRental: next,
        languageId,
      });
      return next;
    });
  }, [guests, languageId, persist, serviceType, visitDate, visitDateRange]);

  const setLanguageId = useCallback((value) => {
    const normalized = normalizeLanguageId(value);
    setLanguageIdState(normalized);
    persist({
      visitDateRange,
      visitDate,
      serviceType,
      guests,
      carRental,
      languageId: normalized,
    });
  }, [carRental, guests, persist, serviceType, visitDate, visitDateRange]);

  const value = useMemo(
    () => ({
      visitDate,
      visitDateRange,
      serviceType,
      guests,
      carRental,
      languageId,
      setVisitDateRange,
      setVisitDate,
      setServiceType,
      setGuests,
      setCarRental,
      setLanguageId,
    }),
    [carRental, guests, languageId, serviceType, setCarRental, setGuests, setLanguageId, setServiceType, setVisitDate, setVisitDateRange, visitDate, visitDateRange]
  );

  return (
    <SiteTripContext.Provider value={value}>{children}</SiteTripContext.Provider>
  );
}

export function useSiteTrip() {
  const ctx = useContext(SiteTripContext);
  if (!ctx) {
    throw new Error('useSiteTrip must be used within SiteTripProvider');
  }
  return ctx;
}
